/**
 * Hydronic Contamination Risk Scoring & Recommendations
 *
 * Evaluates system contamination risk based on symptoms, system characteristics,
 * and water quality. Provides recommendations for powerflush, magnetic filter,
 * and inhibitor treatments, plus LCOO impact calculations.
 */

// ==================== TYPES ====================

export type ContaminationSeverity = 'low' | 'moderate' | 'high' | 'severe';
export type RecommendationStrength = 'none' | 'consider' | 'recommend' | 'strongly_recommend';
export type SystemType = 'sealed' | 'open_vented';
export type PipeworkType = 'copper' | 'barrier_pex' | 'non_barrier_plastic' | 'mixed_unknown';
export type MetalMix = 'low' | 'moderate' | 'high';
export type BoilerHexMaterial = 'stainless' | 'aluminium' | 'cast_iron' | 'unknown';

export interface ContaminationSymptoms {
  radiator_cold_bottom?: boolean;
  kettling_or_whistling?: boolean;
  gurgling_air_or_gas?: boolean;
  pump_noise_or_vibration?: boolean;
  frequent_bleeding?: boolean;
  slow_warmup?: boolean;
  visible_magnetite_on_drain?: boolean;
}

export interface SystemRiskFactors {
  system_type?: SystemType;
  has_magnetic_filter?: boolean;
  pipework?: PipeworkType;
  metal_mix?: MetalMix;
  boiler_hex_material?: BoilerHexMaterial;
  system_age_years?: number | null;
  topups_per_year?: number | null;
  hard_water_area?: boolean | null;
  conductivity_uScm?: number | null;
  system_pH?: number | null;
}

export interface ContaminationInput {
  symptoms: ContaminationSymptoms;
  systemFactors: SystemRiskFactors;
}

export interface ContaminationScoreResult {
  score: number;                    // 0-100
  severity: ContaminationSeverity;
  reasons: string[];                // Top contributing factors
  breakdown: {                      // Detailed point breakdown
    symptom_points: number;
    system_points: number;
    total_capped: number;
  };
}

export interface RemediationRecommendation {
  flush: RecommendationStrength;
  filter: RecommendationStrength;
  inhibitor: RecommendationStrength;
  notes: string[];                  // Contextual advice
}

export interface LcooImpact {
  capex_items: Array<{
    key: string;
    label: string;
    cost_gbp: number;
  }>;
  annual_savings_items: Array<{
    key: string;
    label: string;
    saving_gbp_per_year: number;
  }>;
  meta: {
    penalty_pct: number;                    // Energy penalty (0-0.07)
    p_sludge_breakdown: number;             // Probability of sludge-related breakdown
    combined_reduction: number;             // Combined risk reduction from remediation
    annual_energy_saving_gbp: number;
    annual_breakdown_saving_gbp: number;
  };
}

export interface LcooContext {
  annual_heat_cost_gbp: number;             // From existing LCOO or user input
  avg_breakdown_cost_gbp?: number;          // Default 400 if not provided
  flush_cost_gbp?: number;                  // Default 550 (300-800 range)
  filter_cost_gbp?: number;                 // Default 250 (150-350 range)
  inhibitor_cost_gbp?: number;              // Default 35 (20-50 range)
}

// ==================== CONFIGURATION ====================

export const CONTAMINATION_CONFIG = {
  // Scoring weights - symptoms
  weights: {
    symptoms: {
      radiator_cold_bottom: 25,
      kettling_or_whistling: 20,
      pump_noise_or_vibration: 15,
      gurgling_air_or_gas: 8,
      frequent_bleeding: 12,
      slow_warmup: 10,
      visible_magnetite_on_drain: 30,
    },
    system: {
      open_vented: 18,
      no_magnetic_filter: 15,
      non_barrier_plastic: 12,
      high_metal_mix: 10,
      system_age_per_year: 0.5,       // min(age, 20) * 0.5
      topups_low: 6,                   // 1-2 per year
      topups_high: 14,                 // 3+ per year
      conductivity_high: 12,           // >100 µS/cm
      ph_out_of_range: 10,             // <7.5 or >8.5 (if aluminium)
    },
  },

  // Severity thresholds
  severity: {
    low_max: 19,
    moderate_max: 44,
    high_max: 69,
    // severe: 70-100
  },

  // Severity multipliers for LCOO calculations
  severity_multipliers: {
    energy_penalty: {
      low: 0.00,
      moderate: 0.35,    // -> 2.45% penalty
      high: 0.70,        // -> 4.9% penalty
      severe: 1.00,      // -> 7.0% penalty
    },
    sludge_risk: {
      low: 0.25,
      moderate: 0.60,
      high: 1.00,
      severe: 1.30,
    },
  },

  // Breakdown probability assumptions
  breakdown: {
    p_breakdown_base: 0.15,           // 15% per year baseline
    sludge_attribution: 0.60,         // 60% of breakdowns attributed to sludge
  },

  // Mitigation effectiveness (fractional reduction of sludge breakdown component)
  mitigation: {
    flush: {
      low: 0,
      moderate: 0.25,
      high: 0.40,
      severe: 0.55,
    },
    filter: {
      low: 0,
      moderate: 0.15,
      high: 0.25,
      severe: 0.35,
    },
  },

  // Cost defaults (GBP)
  costs: {
    flush_mid: 550,                   // Range: 300-800
    filter_mid: 250,                  // Range: 150-350
    inhibitor_mid: 35,                // Range: 20-50
    avg_breakdown: 400,               // Average breakdown repair cost
  },

  // Energy penalty assumption
  max_energy_penalty_pct: 0.07,       // 7% max efficiency loss
};

// ==================== SCORING FUNCTION ====================

/**
 * Calculate contamination risk score (0-100) from symptoms and system factors
 */
export function scoreContaminationRisk(input: ContaminationInput): ContaminationScoreResult {
  const { symptoms, systemFactors } = input;
  const config = CONTAMINATION_CONFIG.weights;
  const reasons: string[] = [];
  let symptomPoints = 0;
  let systemPoints = 0;

  // --- Symptom scoring ---
  if (symptoms.radiator_cold_bottom) {
    symptomPoints += config.symptoms.radiator_cold_bottom;
    reasons.push('Radiators cold at bottom (magnetite settlement)');
  }
  if (symptoms.kettling_or_whistling) {
    symptomPoints += config.symptoms.kettling_or_whistling;
    reasons.push('Kettling or whistling sounds (scale/sludge buildup)');
  }
  if (symptoms.pump_noise_or_vibration) {
    symptomPoints += config.symptoms.pump_noise_or_vibration;
    reasons.push('Pump noise or vibration (debris interference)');
  }
  if (symptoms.gurgling_air_or_gas) {
    symptomPoints += config.symptoms.gurgling_air_or_gas;
    reasons.push('Gurgling or air sounds (corrosion gases)');
  }
  if (symptoms.frequent_bleeding) {
    symptomPoints += config.symptoms.frequent_bleeding;
    reasons.push('Frequent bleeding required (hydrogen gas from corrosion)');
  }
  if (symptoms.slow_warmup) {
    symptomPoints += config.symptoms.slow_warmup;
    reasons.push('Slow warmup / poor heat distribution');
  }
  if (symptoms.visible_magnetite_on_drain) {
    symptomPoints += config.symptoms.visible_magnetite_on_drain;
    reasons.push('Visible magnetite on drain (severe contamination)');
  }

  // --- System factor scoring ---
  if (systemFactors.system_type === 'open_vented') {
    systemPoints += config.system.open_vented;
    reasons.push('Open-vented system (higher oxygen ingress risk)');
  }

  if (systemFactors.has_magnetic_filter === false) {
    systemPoints += config.system.no_magnetic_filter;
    reasons.push('No magnetic filter installed');
  }

  if (systemFactors.pipework === 'non_barrier_plastic') {
    systemPoints += config.system.non_barrier_plastic;
    reasons.push('Non-barrier plastic pipework (oxygen permeation)');
  }

  if (systemFactors.metal_mix === 'high') {
    systemPoints += config.system.high_metal_mix;
    reasons.push('High metal mix / dissimilar metals (galvanic corrosion)');
  }

  if (typeof systemFactors.system_age_years === 'number' && systemFactors.system_age_years > 0) {
    const agePoints = Math.min(systemFactors.system_age_years, 20) * config.system.system_age_per_year;
    systemPoints += agePoints;
    if (systemFactors.system_age_years >= 10) {
      reasons.push('System age ' + systemFactors.system_age_years + ' years (accumulated contamination)');
    }
  }

  if (typeof systemFactors.topups_per_year === 'number') {
    if (systemFactors.topups_per_year >= 3) {
      systemPoints += config.system.topups_high;
      reasons.push('Frequent top-ups (≥3/year, introducing fresh oxygen/minerals)');
    } else if (systemFactors.topups_per_year >= 1) {
      systemPoints += config.system.topups_low;
      reasons.push('Occasional top-ups (1-2/year)');
    }
  }

  if (typeof systemFactors.conductivity_uScm === 'number' && systemFactors.conductivity_uScm > 100) {
    systemPoints += config.system.conductivity_high;
    reasons.push('High conductivity (' + systemFactors.conductivity_uScm + ' µS/cm, dissolved solids)');
  }

  // pH check - only relevant if aluminium heat exchanger present
  if (
    systemFactors.boiler_hex_material === 'aluminium' &&
    typeof systemFactors.system_pH === 'number'
  ) {
    if (systemFactors.system_pH < 7.5 || systemFactors.system_pH > 8.5) {
      systemPoints += config.system.ph_out_of_range;
      reasons.push('pH ' + systemFactors.system_pH + ' out of safe range for aluminium (7.5-8.5)');
    }
  }

  // Calculate total score as a percentage (0-100%), ensuring it never drops below 0% or exceeds 100%
  const totalScore = Math.max(0, Math.min(symptomPoints + systemPoints, 100));

  // Determine severity
  let severity: ContaminationSeverity;
  const thresholds = CONTAMINATION_CONFIG.severity;
  if (totalScore <= thresholds.low_max) {
    severity = 'low';
  } else if (totalScore <= thresholds.moderate_max) {
    severity = 'moderate';
  } else if (totalScore <= thresholds.high_max) {
    severity = 'high';
  } else {
    severity = 'severe';
  }

  return {
    score: totalScore,
    severity,
    reasons: reasons.slice(0, 5), // Top 5 reasons
    breakdown: {
      symptom_points: symptomPoints,
      system_points: systemPoints,
      total_capped: totalScore,
    },
  };
}

// ==================== RECOMMENDATION FUNCTION ====================

/**
 * Generate remediation recommendations based on contamination score
 */
export function recommendRemediation(
  input: ContaminationInput,
  scoreResult: ContaminationScoreResult
): RemediationRecommendation {
  const { severity } = scoreResult;
  const { symptoms, systemFactors } = input;
  const notes: string[] = [];

  // --- Powerflush / Cleanse ---
  let flush: RecommendationStrength = 'none';

  if (severity === 'low') {
    flush = 'none';
    // Could add "consider at next service if replacing boiler" as a note
  } else if (severity === 'moderate') {
    // Consider, but recommend if symptoms present
    const hasSymptoms = Object.values(symptoms).some((v) => v === true);
    flush = hasSymptoms ? 'recommend' : 'consider';
    if (flush === 'consider') {
      notes.push('Consider powerflush/cleanse, especially if replacing boiler');
    }
  } else if (severity === 'high') {
    flush = 'recommend';
    notes.push('Powerflush recommended to restore efficiency and protect new components');
  } else if (severity === 'severe') {
    flush = 'strongly_recommend';
    notes.push('Powerflush strongly recommended - severe contamination poses risk to heat exchanger and pump');
  }

  // --- Magnetic Filter ---
  let filter: RecommendationStrength = 'none';

  if (systemFactors.has_magnetic_filter === true) {
    // Already has one - recommend service
    filter = 'recommend'; // Using "recommend" to mean "service/clean"
    notes.push('Existing magnetic filter should be serviced/cleaned and inhibitor checked');
  } else {
    // No filter installed
    if (severity === 'low') {
      filter = 'none';
    } else if (severity === 'moderate') {
      filter = 'recommend';
    } else {
      // high or severe
      filter = 'strongly_recommend';
    }

    // Bump one level if high-risk system type
    const highRiskSystem =
      systemFactors.system_type === 'open_vented' ||
      systemFactors.pipework === 'non_barrier_plastic' ||
      (typeof systemFactors.topups_per_year === 'number' && systemFactors.topups_per_year >= 3);

    if (highRiskSystem && filter === 'none') {
      filter = 'recommend';
    } else if (highRiskSystem && filter === 'recommend') {
      filter = 'strongly_recommend';
    }

    if (filter !== 'none') {
      notes.push('Magnetic filter will capture magnetite and protect system long-term');
    }
  }

  // --- Inhibitor ---
  let inhibitor: RecommendationStrength = 'recommend';

  // Always recommend inhibitor if metal system (which most are)
  const hasMetalComponents =
    systemFactors.metal_mix !== 'low' ||
    systemFactors.pipework === 'copper' ||
    systemFactors.pipework === 'mixed_unknown';

  if (hasMetalComponents) {
    inhibitor = 'recommend';
    notes.push('Inhibitor essential to protect against corrosion in metal systems');
  }

  // Special note for aluminium heat exchangers
  if (systemFactors.boiler_hex_material === 'aluminium') {
    notes.push('⚠️ Aluminium heat exchanger: Use appropriate inhibitor and maintain pH 7.5-8.5');
  }

  return {
    flush,
    filter,
    inhibitor,
    notes,
  };
}

// ==================== LCOO IMPACT FUNCTION ====================

/**
 * Calculate LCOO impacts from contamination remediation
 */
export function estimateLcooImpacts(
  input: ContaminationInput,
  scoreResult: ContaminationScoreResult,
  recommendation: RemediationRecommendation,
  lcooContext: LcooContext
): LcooImpact {
  const { severity } = scoreResult;
  const config = CONTAMINATION_CONFIG;

  // Use provided costs or defaults
  const flushCost = lcooContext.flush_cost_gbp ?? config.costs.flush_mid;
  const filterCost = lcooContext.filter_cost_gbp ?? config.costs.filter_mid;
  const inhibitorCost = lcooContext.inhibitor_cost_gbp ?? config.costs.inhibitor_mid;
  const avgBreakdownCost = lcooContext.avg_breakdown_cost_gbp ?? config.costs.avg_breakdown;

  const capexItems: LcooImpact['capex_items'] = [];
  const annualSavingsItems: LcooImpact['annual_savings_items'] = [];

  // --- 1. Energy savings calculation ---
  const severityMultiplier = config.severity_multipliers.energy_penalty[severity];
  const penaltyPct = config.max_energy_penalty_pct * severityMultiplier;
  const annualEnergySaving = lcooContext.annual_heat_cost_gbp * penaltyPct;

  if (annualEnergySaving > 0) {
    annualSavingsItems.push({
      key: 'energy_efficiency_recovery',
      label: 'Energy efficiency recovery (' + (penaltyPct * 100).toFixed(1) + '% penalty removed)',
      saving_gbp_per_year: annualEnergySaving,
    });
  }

  // --- 2. Breakdown risk reduction calculation ---
  const pBreakdownBase = config.breakdown.p_breakdown_base;
  const sludgeAttribution = config.breakdown.sludge_attribution;
  const pBreakdownSludgeComponent = pBreakdownBase * sludgeAttribution; // 0.15 * 0.60 = 0.09

  const sludgeRiskMultiplier = config.severity_multipliers.sludge_risk[severity];
  const pSludgeBreakdown = pBreakdownSludgeComponent * sludgeRiskMultiplier;

  // Calculate mitigation effectiveness
  let rFlush = 0;
  let rFilter = 0;

  if (recommendation.flush === 'recommend' || recommendation.flush === 'strongly_recommend') {
    rFlush = config.mitigation.flush[severity] || 0;
    capexItems.push({
      key: 'powerflush',
      label: 'Powerflush / MagnaCleanse',
      cost_gbp: flushCost,
    });
  }

  if (
    recommendation.filter === 'recommend' ||
    recommendation.filter === 'strongly_recommend'
  ) {
    // Only add filter capex if not already installed
    if (input.systemFactors.has_magnetic_filter !== true) {
      rFilter = config.mitigation.filter[severity] || 0;
      capexItems.push({
        key: 'magnetic_filter',
        label: 'Magnetic filter supply + fit',
        cost_gbp: filterCost,
      });
    }
  }

  if (recommendation.inhibitor === 'recommend') {
    capexItems.push({
      key: 'inhibitor',
      label: 'System inhibitor',
      cost_gbp: inhibitorCost,
    });
  }

  // Combined reduction (don't double-count)
  const combinedReduction = 1 - (1 - rFlush) * (1 - rFilter);

  const annualBreakdownSaving = pSludgeBreakdown * combinedReduction * avgBreakdownCost;

  if (annualBreakdownSaving > 0) {
    annualSavingsItems.push({
      key: 'breakdown_risk_reduction',
      label: 'Avoided breakdown costs (' + (combinedReduction * 100).toFixed(0) + '% risk reduction)',
      saving_gbp_per_year: annualBreakdownSaving,
    });
  }

  return {
    capex_items: capexItems,
    annual_savings_items: annualSavingsItems,
    meta: {
      penalty_pct: penaltyPct,
      p_sludge_breakdown: pSludgeBreakdown,
      combined_reduction: combinedReduction,
      annual_energy_saving_gbp: annualEnergySaving,
      annual_breakdown_saving_gbp: annualBreakdownSaving,
    },
  };
}

// ==================== CONVENIENCE WRAPPER ====================

export interface ContaminationAnalysis {
  score: ContaminationScoreResult;
  recommendation: RemediationRecommendation;
  lcooImpact: LcooImpact;
}

/**
 * All-in-one function to analyze contamination, recommend remediation, and estimate LCOO
 */
export function analyzeContamination(
  input: ContaminationInput,
  lcooContext: LcooContext
): ContaminationAnalysis {
  const score = scoreContaminationRisk(input);
  const recommendation = recommendRemediation(input, score);
  const lcooImpact = estimateLcooImpacts(input, score, recommendation, lcooContext);

  return {
    score,
    recommendation,
    lcooImpact,
  };
}
